import { Component, Input, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { LucideAngularModule } from 'lucide-angular';
import { QuotesService } from '../../../../../core/data/services/quotes.service';
import { SwalService } from '../../../../../common/services/swal.service';
import { lastValueFrom } from 'rxjs';
import { QuoteDetailDto } from '../../../../../core/data/dtos/quote-detail.dto';

@Component({
  selector: 'app-download-quote-modal',
  standalone: true,
  imports: [ReactiveFormsModule, LucideAngularModule],
  template: `
    <div class="modal-header border-0 pb-0">
      <h5 class="modal-title fw-bold text-danger">Descargar cotización</h5>
      <button
        type="button"
        class="btn-close"
        aria-label="Close"
        (click)="activeModal.dismiss()"
      ></button>
    </div>
    <div class="modal-body">
      <p class="text-muted small mb-4">
        Por favor, completa tus datos para descargar la cotización. Podrás usar
        tu correo y número de cotización para retomarla más tarde.
      </p>

      <form [formGroup]="form">
        <div class="mb-3">
          <label
            for="fullName"
            class="form-label small fw-semibold text-uppercase text-muted"
            >Nombre completo</label
          >
          <input
            type="text"
            class="form-control focus-ring focus-ring-danger"
            id="fullName"
            formControlName="fullName"
            placeholder="Tu nombre"
          />
        </div>
        <div class="mb-3">
          <label
            for="email"
            class="form-label small fw-semibold text-uppercase text-muted"
            >Correo electrónico</label
          >
          <input
            type="email"
            class="form-control focus-ring focus-ring-danger"
            id="email"
            formControlName="email"
            placeholder="nombre@ejemplo.com"
          />
        </div>
      </form>
    </div>
    <div
      class="modal-footer border-0 pb-0 pt-2 justify-content-between align-items-center"
    >
      <div class="text-muted small fw-semibold">Cotización #{{ quoteId }}</div>
      <button
        type="button"
        class="btn btn-primary rounded-pill px-4"
        [disabled]="form.invalid || saving"
        (click)="onSubmit()"
      >
        @if (saving) {
          <span
            class="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          ></span>
          Guardando...
        } @else {
          Descargar
        }
      </button>
    </div>
  `,
  styles: [
    `
      .form-control {
        border-radius: 0.5rem;
        padding: 0.75rem 1rem;
      }
      .modal-title {
        font-size: 1.25rem;
      }
    `,
  ],
})
export class DownloadQuoteModalComponent {
  activeModal = inject(NgbActiveModal);
  @Input() quoteId: number = 0;
  @Input() initialEmail: string = '';
  saving = false;
  private fb = inject(FormBuilder);
  form = this.fb.group({
    fullName: ['', [Validators.required, Validators.minLength(3)]],
    email: ['', [Validators.required, Validators.email]],
  });
  private quotesService = inject(QuotesService);
  private swal = inject(SwalService);

  ngOnInit() {
    if (this.initialEmail) {
      this.form.patchValue({ email: this.initialEmail });
    }
  }

  async onSubmit() {
    if (this.form.invalid) return;

    this.saving = true;
    const { fullName, email } = this.form.value;

    // Split name into first and last name
    const nameParts = (fullName || '').trim().split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');

    try {
      const updateDto = {
        quoteEmail: email,
        requestedByName: firstName,
        requestedByLastName: lastName,
      };

      await lastValueFrom(
        this.quotesService.updateQuote(this.quoteId, updateDto as any),
      );

      // Download PDF
      const blob = await lastValueFrom(
        this.quotesService.downloadQuotePdf(this.quoteId),
      );
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `Cotizacion-${this.quoteId}.pdf`;
      anchor.click();
      window.URL.revokeObjectURL(url);

      this.saving = false;
      this.activeModal.close(true);
    } catch (error) {
      this.saving = false;
      this.swal.error(
        'Ocurrió un error al procesar la solicitud. Por favor intenta de nuevo.',
      );
      console.error('Error processing quote download:', error);
    }
  }
}
